import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGavel, faPlus, faEdit, faTrash, faEye, faClock, faCheckCircle, 
  faBan, faTrophy, faSearch, faFilter, faCalendarAlt, faUsers,
  faEuroSign, faPlay, faTimes, faStopCircle
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import { initializeSocket, joinAuction, leaveAuction, onAuctionUpdate, offAuctionUpdate, joinAuctionsList, leaveAuctionsList, onAuctionsListUpdate, offAuctionsListUpdate } from '../../services/socketService';

interface Auction {
  _id: string;
  auctionNumber: string;
  title: string;
  description?: string;
  masterArticleId: {
    _id: string;
    masterArticleNumber: string;
    name: string;
    category?: string;
  };
  masterArticleNumber: string;
  masterArticleName: string;
  validFrom: string;
  validUntil: string;
  calendarWeek: number;
  year: number;
  biddingStartTime: string;
  biddingEndTime: string;
  minimumBidIncrement: number;
  startingPrice?: number;
  reservePrice?: number;
  estimatedQuantity?: number;
  unit: string;
  eligibleSuppliers: Array<{
    supplierId: string;
    supplierName: string;
    creditorNumber: string;
    hasViewed: boolean;
  }>;
  currentLowestBid?: number;
  currentLowestBidder?: string;
  totalBidsCount: number;
  status: 'draft' | 'scheduled' | 'active' | 'ended' | 'awarded' | 'cancelled';
  winningSupplierId?: string;
  winningBidAmount?: number;
  awardedAt?: string;
  createdAt: string;
}

interface MasterArticle {
  _id: string;
  masterArticleNumber: string;
  name: string;
  category?: string;
  isAuctionable: boolean;
  unit?: string;
}

const AuctionManagement: React.FC = () => {
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAuction, setEditAuction] = useState<Auction | null>(null);
  const [selectedAuction, setSelectedAuction] = useState<Auction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [auctionToCancel, setAuctionToCancel] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second for countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadAuctions();
    loadStats();

    // Initialize Socket.IO for live updates
    initializeSocket();
    joinAuctionsList();

    const handleAuctionsUpdate = () => {
      console.log('📢 [ADMIN] Received auctions-list-updated event');
      loadAuctions();
      loadStats();
    };

    onAuctionsListUpdate(handleAuctionsUpdate);

    return () => {
      offAuctionsListUpdate(handleAuctionsUpdate);
      leaveAuctionsList();
    };
  }, [statusFilter]);

  // Subscribe to individual auction updates for real-time bid updates
  useEffect(() => {
    if (auctions.length === 0) return;

    console.log(`🔗 [ADMIN] Joining ${auctions.length} auction rooms for real-time updates`);

    // Join all auction rooms
    auctions.forEach(auction => {
      joinAuction(auction._id);
    });

    // Listen for individual auction updates
    const handleIndividualAuctionUpdate = async (data: any) => {
      console.log('📢 [ADMIN] Individual auction updated:', data);
      
      // Update only the specific auction in the list
      setAuctions(prevAuctions => 
        prevAuctions.map(auction => {
          if (auction._id === data.auctionId) {
            console.log(`  ✅ [ADMIN] Updating auction ${auction._id}:`, {
              oldBid: auction.currentLowestBid,
              newBid: data.currentLowestBid,
              oldCount: auction.totalBidsCount,
              newCount: data.totalBidsCount
            });
            return {
              ...auction,
              currentLowestBid: data.currentLowestBid ?? auction.currentLowestBid,
              currentLowestBidder: data.currentLowestBidder ?? auction.currentLowestBidder,
              totalBidsCount: data.totalBidsCount ?? auction.totalBidsCount
            };
          }
          return auction;
        })
      );

      // Update stats
      loadStats();
    };

    onAuctionUpdate(handleIndividualAuctionUpdate);

    return () => {
      offAuctionUpdate(handleIndividualAuctionUpdate);
      auctions.forEach(auction => {
        leaveAuction(auction._id);
      });
    };
  }, [auctions.map(a => a._id).join(',')]);


  const loadAuctions = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (statusFilter) params.status = statusFilter;

      const response = await api.get('/admin/auctions', { params });
      setAuctions(response.data.auctions);
    } catch (error) {
      console.error('Error loading auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/auctions/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleActivate = async (id: string) => {
    if (!window.confirm('Auktion aktivieren?')) return;

    try {
      await api.post(`/admin/auctions/${id}/activate`);
      loadAuctions();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancel = (id: string) => {
    setAuctionToCancel(id);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!auctionToCancel || !cancelReason.trim()) {
      alert('Bitte geben Sie einen Grund für den Abbruch ein.');
      return;
    }

    try {
      await api.post(`/admin/auctions/${auctionToCancel}/cancel`, { reason: cancelReason });
      setShowCancelModal(false);
      setAuctionToCancel(null);
      setCancelReason('');
      loadAuctions();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEarlyEnd = async (id: string) => {
    if (!window.confirm('Möchten Sie diese Auktion vorzeitig beenden? Der Lieferant mit dem niedrigsten Gebot gewinnt.')) return;

    try {
      await api.post(`/admin/auctions/${id}/early-end`);
      loadAuctions();
      alert('Auktion wurde vorzeitig beendet und dem niedrigsten Bieter zugeschlagen.');
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAward = async (id: string) => {
    setSelectedAuction(auctions.find(a => a._id === id) || null);
    setShowDetailModal(true);
  };

  const getStatusBadge = (status: string) => {
    const badges: any = {
      draft: { color: 'gray', icon: faEdit, text: 'Entwurf' },
      scheduled: { color: 'blue', icon: faClock, text: 'Geplant' },
      active: { color: 'green', icon: faPlay, text: 'Aktiv' },
      ended: { color: 'yellow', icon: faClock, text: 'Beendet' },
      awarded: { color: 'purple', icon: faTrophy, text: 'Vergeben' },
      cancelled: { color: 'red', icon: faBan, text: 'Abgebrochen' }
    };

    const badge = badges[status] || badges.draft;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${badge.color}-100 text-${badge.color}-800`}>
        <FontAwesomeIcon icon={badge.icon} className="mr-1" />
        {badge.text}
      </span>
    );
  };

  return (
    <div className="w-full px-6 py-4">
      {/* Stats with Button */}
      {stats && (
        <div className="flex items-center gap-3 mb-4">
          <div className="grid grid-cols-5 gap-3 flex-1">
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-sm border border-gray-200 px-4 py-3">
              <div className="text-xs text-gray-500 font-medium uppercase">Gesamt</div>
              <div className="text-2xl font-bold text-gray-800">{stats.totalAuctions}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 px-4 py-3">
              <div className="text-xs text-blue-600 font-medium uppercase">Geplant</div>
              <div className="text-2xl font-bold text-blue-800">{stats.scheduledAuctions}</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 px-4 py-3">
              <div className="text-xs text-green-600 font-medium uppercase">Aktiv</div>
              <div className="text-2xl font-bold text-green-800">{stats.activeAuctions}</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg shadow-sm border border-yellow-200 px-4 py-3">
              <div className="text-xs text-yellow-600 font-medium uppercase">Beendet</div>
              <div className="text-2xl font-bold text-yellow-800">{stats.endedAuctions}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow-sm border border-purple-200 px-4 py-3">
              <div className="text-xs text-purple-600 font-medium uppercase">Vergeben</div>
              <div className="text-2xl font-bold text-purple-800">{stats.awardedAuctions}</div>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-3 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faPlus} />
            Neue Auktion
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4">
        <div className="flex gap-3 items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-2 whitespace-nowrap">
              <FontAwesomeIcon icon={faFilter} />
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Alle Status</option>
              <option value="draft">Entwurf</option>
              <option value="scheduled">Geplant</option>
              <option value="active">Aktiv</option>
              <option value="ended">Beendet</option>
              <option value="awarded">Vergeben</option>
              <option value="cancelled">Abgebrochen</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2 flex-1">
            <label className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-2 whitespace-nowrap">
              <FontAwesomeIcon icon={faSearch} />
              Suche
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Auktion, Artikel oder Lieferant suchen..."
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <button
            onClick={() => {
              setStatusFilter('');
              setSearchQuery('');
            }}
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors whitespace-nowrap"
          >
            Zurücksetzen
          </button>
        </div>
      </div>

      {/* Auctions List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Auktionen...</p>
        </div>
      ) : (() => {
        // Filter auctions based on status and search query
        const filteredAuctions = auctions.filter(auction => {
          const matchesStatus = !statusFilter || auction.status === statusFilter;
          const matchesSearch = !searchQuery || 
            auction.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            auction.masterArticleName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            auction.masterArticleNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            auction.auctionNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            auction.eligibleSuppliers.some(s => s.supplierName.toLowerCase().includes(searchQuery.toLowerCase()));
          return matchesStatus && matchesSearch;
        });

        return filteredAuctions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FontAwesomeIcon icon={faGavel} className="text-6xl text-gray-300 mb-4" />
            <p className="text-gray-600">Keine Auktionen gefunden</p>
            {(statusFilter || searchQuery) && (
              <p className="text-sm text-gray-500 mt-2">Versuchen Sie, die Filter zurückzusetzen</p>
            )}
          </div>
        ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Auktion</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Zeitraum</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Teilnehmer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Niedrigstes Gebot</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAuctions.map((auction) => (
                <tr key={auction._id} className="hover:bg-orange-50 transition-colors">
                  {/* Auktion Info */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-gray-800">{auction.title}</h3>
                          {getStatusBadge(auction.status)}
                        </div>
                        <div className="text-xs text-gray-500">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                          KW {auction.calendarWeek}/{auction.year} • {auction.auctionNumber}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {auction.masterArticleNumber} - {auction.masterArticleName}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Zeitraum */}
                  <td className="px-4 py-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-16">Start:</span>
                        <span className="text-sm font-medium text-gray-800">
                          {new Date(auction.biddingStartTime).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-16">Ende:</span>
                        <span className="text-sm font-medium text-gray-800">
                          {new Date(auction.biddingEndTime).toLocaleDateString('de-DE')}
                        </span>
                        {auction.status === 'active' && (() => {
                          const now = currentTime.getTime();
                          const end = new Date(auction.biddingEndTime).getTime();
                          const diff = end - now;
                          if (diff > 0) {
                            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                            return (
                              <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                {days}T {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </td>

                  {/* Teilnehmer */}
                  <td className="px-4 py-4">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2 text-orange-600">
                        <FontAwesomeIcon icon={faUsers} className="text-lg" />
                        <span className="text-lg font-bold">{auction.eligibleSuppliers.length}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {auction.totalBidsCount} {auction.totalBidsCount === 1 ? 'Gebot' : 'Gebote'}
                      </div>
                    </div>
                  </td>

                  {/* Niedrigstes Gebot */}
                  <td className="px-4 py-4">
                    {auction.currentLowestBid ? (
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faTrophy} className="text-lg text-yellow-500" />
                        <div>
                          <div className="text-xl font-bold text-green-700">
                            € {auction.currentLowestBid.toFixed(2).replace('.', ',')}
                          </div>
                          {auction.currentLowestBidder && (() => {
                            const supplier = auction.eligibleSuppliers.find(
                              s => s.supplierId === auction.currentLowestBidder
                            );
                            return supplier ? (
                              <div className="text-xs text-gray-600">
                                {supplier.supplierName}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic">Noch keine Gebote</div>
                    )}
                  </td>

                  {/* Aktionen */}
                  <td className="px-4 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setSelectedAuction(auction);
                          setShowDetailModal(true);
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
                        title="Details & Gebote"
                      >
                        <FontAwesomeIcon icon={faEye} />
                        Details
                      </button>

                      {auction.status === 'draft' || auction.status === 'scheduled' ? (
                        <>
                          <button
                            onClick={() => {
                              setEditAuction(auction);
                              setShowEditModal(true);
                            }}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
                            title="Bearbeiten"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleActivate(auction._id)}
                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
                            title="Aktivieren"
                          >
                            <FontAwesomeIcon icon={faPlay} />
                          </button>
                        </>
                      ) : null}

                      {auction.status === 'active' || auction.status === 'scheduled' ? (
                        <>
                          {auction.status === 'active' && auction.currentLowestBid && (
                            <button
                              onClick={() => handleEarlyEnd(auction._id)}
                              className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
                              title="Vorzeitig beenden"
                            >
                              <FontAwesomeIcon icon={faStopCircle} />
                              Beenden
                            </button>
                          )}
                          <button
                            onClick={() => handleCancel(auction._id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
                            title="Abbrechen"
                          >
                            <FontAwesomeIcon icon={faBan} />
                          </button>
                        </>
                      ) : null}

                      {auction.status === 'ended' && !auction.winningSupplierId ? (
                        <button
                          onClick={() => handleAward(auction._id)}
                          className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
                          title="Vergeben"
                        >
                          <FontAwesomeIcon icon={faTrophy} />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        );
      })()}

      {/* Create Modal */}
      {showCreateModal && (
        <AuctionCreateModal
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            loadAuctions();
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editAuction && (
        <AuctionEditModal
          auction={editAuction}
          onClose={() => {
            setShowEditModal(false);
            setEditAuction(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setEditAuction(null);
            loadAuctions();
          }}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAuction && (
        <AuctionDetailModal
          auction={selectedAuction}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAuction(null);
          }}
          onUpdate={() => {
            loadAuctions();
          }}
        />
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-red-500 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FontAwesomeIcon icon={faBan} />
                Auktion abbrechen
              </h2>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setAuctionToCancel(null);
                  setCancelReason('');
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-2xl" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Bitte geben Sie einen Grund für den Abbruch der Auktion an. Alle beteiligten Lieferanten werden benachrichtigt.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Grund für Abbruch <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="z.B. Artikel nicht mehr verfügbar, Preisanpassung erforderlich, etc."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setAuctionToCancel(null);
                    setCancelReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg"
                >
                  Zurück
                </button>
                <button
                  onClick={handleCancelConfirm}
                  disabled={!cancelReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faBan} className="mr-2" />
                  Auktion abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Modal Component
const AuctionCreateModal: React.FC<{
  onClose: () => void;
  onSave: () => void;
}> = ({ onClose, onSave }) => {
  const [masterArticles, setMasterArticles] = useState<MasterArticle[]>([]);
  const currentYear = new Date().getFullYear();
  const currentWeek = Math.ceil((((new Date().getTime() - new Date(currentYear, 0, 1).getTime()) / 86400000) + new Date(currentYear, 0, 1).getDay() + 1) / 7);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    masterArticleId: '',
    calendarWeek: currentWeek + 1, // Nächste Woche als Standard
    year: currentYear,
    biddingStartTime: '',
    biddingEndTime: '',
    minimumBidIncrement: 0.01,
    startingPrice: '',
    reservePrice: '',
    estimatedQuantity: '',
    unit: 'KG'
  });
  const [selectedSuppliers, setSelectedSuppliers] = useState<Set<string>>(new Set());
  const [availableSuppliers, setAvailableSuppliers] = useState<any[]>([]);

  useEffect(() => {
    loadMasterArticles();
  }, []);

  const loadMasterArticles = async () => {
    try {
      const response = await api.get('/admin/master-articles', {
        params: { isAuctionable: true, limit: 200 }
      });
      setMasterArticles(response.data.masterArticles);
    } catch (error) {
      console.error('Error loading master articles:', error);
    }
  };

  const loadSuppliersForMasterArticle = async (masterArticleId: string) => {
    if (!masterArticleId) {
      setAvailableSuppliers([]);
      setSelectedSuppliers(new Set());
      return;
    }
    
    try {
      const response = await api.get(`/admin/master-articles/${masterArticleId}`);
      console.log('Master article response:', response.data);
      
      const linkedArticles = response.data.linkedSupplierArticles || [];
      const authorizedSuppliers = response.data.authorizedSuppliers || [];
      console.log('Linked articles:', linkedArticles);
      console.log('Authorized suppliers:', authorizedSuppliers);
      
      // Lieferanten mit verknüpften Artikeln
      const suppliersFromArticles = linkedArticles
        .filter((link: any) => link.isActive)
        .map((link: any) => {
          const supplierId = link.supplier?._id || link.supplierId;
          const supplierName = link.supplier?.name || link.supplierName;
          const creditorNumber = link.supplier?.kreditorNumber || link.creditorNumber;
          
          return {
            supplierId,
            supplierName,
            creditorNumber
          };
        })
        .filter((s: any) => s.supplierId);
      
      // Autorisierte Lieferanten (ohne Artikel)
      const suppliersFromAuthorized = authorizedSuppliers
        .filter((auth: any) => auth.isActive)
        .map((auth: any) => ({
          supplierId: auth.supplierId,
          supplierName: auth.supplierName,
          creditorNumber: auth.kreditorNumber
        }));
      
      // Kombiniere beide Listen und entferne Duplikate
      const allSuppliers = [...suppliersFromArticles, ...suppliersFromAuthorized];
      const uniqueSuppliers = allSuppliers.filter((supplier, index, self) => 
        index === self.findIndex((s) => s.supplierId === supplier.supplierId)
      );
      
      console.log('Processed suppliers:', uniqueSuppliers);
      setAvailableSuppliers(uniqueSuppliers);
      
      // Automatisch alle Lieferanten auswählen
      const supplierIds = uniqueSuppliers.map((s: any) => s.supplierId);
      console.log('Selected supplier IDs:', supplierIds);
      setSelectedSuppliers(new Set(supplierIds));
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedSuppliers.size === 0) {
      alert('Bitte wählen Sie mindestens einen Lieferanten aus');
      return;
    }
    
    try {
      const eligibleSuppliersList = Array.from(selectedSuppliers).map(supplierId => {
        const supplier = availableSuppliers.find(s => s.supplierId === supplierId);
        return {
          supplierId,
          supplierName: supplier?.supplierName || '',
          creditorNumber: supplier?.creditorNumber || ''
        };
      });
      
      console.log('Sending eligible suppliers:', eligibleSuppliersList);
      
      const auctionData = {
        ...formData,
        eligibleSuppliers: eligibleSuppliersList
      };
      
      console.log('Auction data:', auctionData);
      
      await api.post('/admin/auctions', auctionData);
      onSave();
    } catch (error: any) {
      console.error('Error creating auction:', error);
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl m-4">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faGavel} className="text-orange-500" />
          Neue Auktion erstellen
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titel *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="z.B. Karotten 5KG - KW 8/2026"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Master-Artikel *
            </label>
            <select
              value={formData.masterArticleId}
              onChange={(e) => {
                setFormData({ ...formData, masterArticleId: e.target.value });
                loadSuppliersForMasterArticle(e.target.value);
              }}
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Bitte wählen...</option>
              {masterArticles.map(article => (
                <option key={article._id} value={article._id}>
                  {article.masterArticleNumber} - {article.name}
                </option>
              ))}
            </select>
          </div>

          {/* Lieferanten-Auswahl */}
          {availableSuppliers.length > 0 && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teilnehmende Lieferanten ({selectedSuppliers.size} von {availableSuppliers.length})
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableSuppliers.map(supplier => (
                  <label key={supplier.supplierId} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.has(supplier.supplierId)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedSuppliers);
                        if (e.target.checked) {
                          newSelected.add(supplier.supplierId);
                        } else {
                          newSelected.delete(supplier.supplierId);
                        }
                        setSelectedSuppliers(newSelected);
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">
                      {supplier.supplierName} ({supplier.creditorNumber})
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Kalenderwoche */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kalenderwoche *
            </label>
            <div className="flex gap-2">
              <select
                value={formData.calendarWeek}
                onChange={(e) => setFormData({ ...formData, calendarWeek: parseInt(e.target.value) })}
                required
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                {Array.from({ length: 53 }, (_, i) => i + 1).map(week => (
                  <option key={week} value={week}>
                    KW {week}
                  </option>
                ))}
              </select>
              <select
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                required
                className="w-28 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value={currentYear}>{currentYear}</option>
                <option value={currentYear + 1}>{currentYear + 1}</option>
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Die Lieferperiode wird automatisch basierend auf der KW berechnet
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gebotsstart *
              </label>
              <input
                type="datetime-local"
                value={formData.biddingStartTime}
                onChange={(e) => setFormData({ ...formData, biddingStartTime: e.target.value })}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gebotsende *
              </label>
              <input
                type="datetime-local"
                value={formData.biddingEndTime}
                onChange={(e) => setFormData({ ...formData, biddingEndTime: e.target.value })}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschreibung
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
            >
              Auktion erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Modal Component
const AuctionEditModal: React.FC<{
  auction: Auction;
  onClose: () => void;
  onSave: () => void;
}> = ({ auction, onClose, onSave }) => {
  const [masterArticles, setMasterArticles] = useState<MasterArticle[]>([]);
  const [formData, setFormData] = useState({
    title: auction.title,
    description: auction.description || '',
    masterArticleId: typeof auction.masterArticleId === 'object' ? auction.masterArticleId._id : auction.masterArticleId,
    validFrom: auction.validFrom.split('T')[0],
    validUntil: auction.validUntil.split('T')[0],
    biddingStartTime: auction.biddingStartTime.substring(0, 16),
    biddingEndTime: auction.biddingEndTime.substring(0, 16),
    minimumBidIncrement: auction.minimumBidIncrement,
    startingPrice: auction.startingPrice?.toString() || '',
    reservePrice: auction.reservePrice?.toString() || '',
    estimatedQuantity: auction.estimatedQuantity?.toString() || '',
    unit: auction.unit || 'KG'
  });
  const [selectedSuppliers, setSelectedSuppliers] = useState<Set<string>>(
    new Set(auction.eligibleSuppliers.map(s => s.supplierId))
  );
  const [availableSuppliers, setAvailableSuppliers] = useState<any[]>([]);

  useEffect(() => {
    loadMasterArticles();
    loadSuppliersForMasterArticle(formData.masterArticleId);
  }, []);

  const loadMasterArticles = async () => {
    try {
      const response = await api.get('/admin/master-articles', {
        params: { isAuctionable: true, limit: 200 }
      });
      setMasterArticles(response.data.masterArticles);
    } catch (error) {
      console.error('Error loading master articles:', error);
    }
  };

  const loadSuppliersForMasterArticle = async (masterArticleId: string) => {
    if (!masterArticleId) {
      setAvailableSuppliers([]);
      return;
    }
    
    try {
      const response = await api.get(`/admin/master-articles/${masterArticleId}`);
      const linkedArticles = response.data.linkedSupplierArticles || [];
      const authorizedSuppliers = response.data.authorizedSuppliers || [];
      
      // Lieferanten mit verknüpften Artikeln
      const suppliersFromArticles = linkedArticles
        .filter((link: any) => link.isActive)
        .map((link: any) => ({
          supplierId: link.supplier?._id || link.supplierId,
          supplierName: link.supplier?.name || link.supplierName,
          creditorNumber: link.supplier?.kreditorNumber || link.creditorNumber
        }))
        .filter((s: any) => s.supplierId);
      
      // Autorisierte Lieferanten (ohne Artikel)
      const suppliersFromAuthorized = authorizedSuppliers
        .filter((auth: any) => auth.isActive)
        .map((auth: any) => ({
          supplierId: auth.supplierId,
          supplierName: auth.supplierName,
          creditorNumber: auth.kreditorNumber
        }));
      
      // Kombiniere beide Listen und entferne Duplikate
      const allSuppliers = [...suppliersFromArticles, ...suppliersFromAuthorized];
      const uniqueSuppliers = allSuppliers.filter((supplier, index, self) => 
        index === self.findIndex((s) => s.supplierId === supplier.supplierId)
      );
      
      setAvailableSuppliers(uniqueSuppliers);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedSuppliers.size === 0) {
      alert('Bitte wählen Sie mindestens einen Lieferanten aus');
      return;
    }
    
    try {
      const eligibleSuppliersList = Array.from(selectedSuppliers).map(supplierId => {
        const supplier = availableSuppliers.find(s => s.supplierId === supplierId);
        return {
          supplierId,
          supplierName: supplier?.supplierName || '',
          creditorNumber: supplier?.creditorNumber || ''
        };
      });
      
      const auctionData = {
        ...formData,
        eligibleSuppliers: eligibleSuppliersList
      };
      
      await api.put(`/admin/auctions/${auction._id}`, auctionData);
      onSave();
    } catch (error: any) {
      console.error('Error updating auction:', error);
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl m-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faEdit} className="text-blue-500" />
          Auktion bearbeiten
        </h2>
        
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Hinweis:</strong> Nur Entwürfe und geplante Auktionen können bearbeitet werden.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titel *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Master-Artikel *
            </label>
            <select
              value={formData.masterArticleId}
              onChange={(e) => {
                setFormData({ ...formData, masterArticleId: e.target.value });
                loadSuppliersForMasterArticle(e.target.value);
              }}
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Bitte wählen...</option>
              {masterArticles.map(article => (
                <option key={article._id} value={article._id}>
                  {article.masterArticleNumber} - {article.name}
                </option>
              ))}
            </select>
          </div>

          {/* Lieferanten-Auswahl */}
          {availableSuppliers.length > 0 && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teilnehmende Lieferanten ({selectedSuppliers.size} von {availableSuppliers.length})
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableSuppliers.map(supplier => (
                  <label key={supplier.supplierId} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.has(supplier.supplierId)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedSuppliers);
                        if (e.target.checked) {
                          newSelected.add(supplier.supplierId);
                        } else {
                          newSelected.delete(supplier.supplierId);
                        }
                        setSelectedSuppliers(newSelected);
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">
                      {supplier.supplierName} ({supplier.creditorNumber})
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lieferperiode von *
              </label>
              <input
                type="date"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lieferperiode bis *
              </label>
              <input
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gebotsstart *
              </label>
              <input
                type="datetime-local"
                value={formData.biddingStartTime}
                onChange={(e) => setFormData({ ...formData, biddingStartTime: e.target.value })}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gebotsende *
              </label>
              <input
                type="datetime-local"
                value={formData.biddingEndTime}
                onChange={(e) => setFormData({ ...formData, biddingEndTime: e.target.value })}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschreibung
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
            >
              Änderungen speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Detail Modal Component (simplified - you'll expand with bids list and award functionality)
const AuctionDetailModal: React.FC<{
  auction: Auction;
  onClose: () => void;
  onUpdate: () => void;
}> = ({ auction, onClose, onUpdate }) => {
  const [bids, setBids] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBids();
  }, []);

  const loadBids = async () => {
    try {
      const response = await api.get(`/admin/auctions/${auction._id}/bids`);
      setBids(response.data.bids);
    } catch (error) {
      console.error('Error loading bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAward = async (supplierId: string, bidAmount: number) => {
    const comment = window.prompt('Kommentar zur Vergabe (optional):');
    
    try {
      await api.post(`/admin/auctions/${auction._id}/award`, {
        winningSupplierId: supplierId,
        comment
      });
      onUpdate();
      onClose();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl m-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-2xl font-bold">{auction.title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            {auction.auctionNumber} • KW {auction.calendarWeek}/{auction.year}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500">Lieferperiode</div>
              <div className="text-sm font-medium">
                {new Date(auction.validFrom).toLocaleDateString('de-DE')} - {new Date(auction.validUntil).toLocaleDateString('de-DE')}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Teilnehmende Lieferanten</div>
              <div className="text-sm font-medium">{auction.eligibleSuppliers.length}</div>
            </div>
          </div>
        </div>

        <h3 className="text-lg font-bold mb-3">Gebote ({bids.length})</h3>
        
        {loading ? (
          <div className="text-center py-8">Lade Gebote...</div>
        ) : bids.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Noch keine Gebote</div>
        ) : (
          <div className="space-y-2">
            {bids.map((bid, index) => (
              <div key={bid._id} className={`p-4 rounded-lg border ${bid.isCurrentLowest ? 'bg-green-50 border-green-300' : 'bg-gray-50'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium">
                      {bid.supplierName}
                      {bid.isCurrentLowest && (
                        <span className="ml-2 text-xs text-green-600 font-bold">
                          <FontAwesomeIcon icon={faTrophy} /> NIEDRIGSTES GEBOT
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(bid.submittedAt).toLocaleString('de-DE')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">
                      € {bid.bidAmount.toFixed(2)}
                    </div>
                    {auction.status === 'ended' && !auction.winningSupplierId && bid.status === 'winning' && (
                      <button
                        onClick={() => handleAward(bid.supplierId, bid.bidAmount)}
                        className="mt-2 px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded"
                      >
                        <FontAwesomeIcon icon={faTrophy} className="mr-1" />
                        Zuschlag erteilen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuctionManagement;
